import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Copy, Check, TrendingUp, Wallet, Target, Trophy, Receipt,
  LayoutDashboard, Package,
} from 'lucide-react';
import { useRyanAuth } from '../context/RyanAuthContext';
import RyanWeeklyBarChart, { EvolutionPoint } from '../components/RyanWeeklyBarChart';
import RyanMonthlyLineChart, { MonthPoint } from '../components/RyanMonthlyLineChart';
import './RyanAmbassadorPage.css';

const tok = () => localStorage.getItem('accessToken') || '';
const cents = (c: number) => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`;
const fmtDateOnly = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const fmtTimeOnly = (d: string | null) => d ? new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—';

// Conta de 0 até o valor alvo com easing suave — dispara quando o alvo chega (dado carregado).
function useCountUp(target: number, duration = 1100) {
  const [value, setValue] = useState(0);
  const prevTarget = useRef<number | null>(null);

  useEffect(() => {
    if (prevTarget.current === target) return;
    prevTarget.current = target;
    let raf: number;
    const start = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setValue(Math.round(target * ease(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}

interface TierStep {
  minSales: number;
  percent: number;
  reached: boolean;
  current: boolean;
}

interface AmbassadorProduct {
  productId: string;
  title: string;
  periodo: number | null;
  link: string | null;
  salesCount: number;
  currentTierPercent: number;
  nextTierPercent: number | null;
  nextTierRemaining: number | null;
  totalEarnedCents: number;
  totalPaidCents: number;
  totalPendingCents: number;
  tiers: TierStep[];
}

interface AmbassadorSummary {
  totalSales: number;
  totalEarnedCents: number;
  totalPaidCents: number;
  totalPendingCents: number;
}

interface TopProduct {
  productId: string;
  title: string;
  salesCount: number;
  totalEarnedCents: number;
}

interface PayoutEntry {
  _id: string;
  amountCents: number;
  salesCount: number;
  paidAt: string | null;
  note: string;
  legacy: boolean;
}

interface AmbassadorData {
  name: string;
  referralCode: string;
  summary: AmbassadorSummary;
  topProducts: TopProduct[];
  payoutHistory: PayoutEntry[];
  products: AmbassadorProduct[];
}

interface Metrics {
  weekly: EvolutionPoint[];
  monthly: MonthPoint[];
}

type Tab = 'painel' | 'produtos' | 'historico';

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  target: number;
  money?: boolean;
  label: string;
  variant?: 'neutral' | 'mint' | 'blue' | 'highlight';
}> = ({ icon, target, money, label, variant = 'neutral' }) => {
  const value = useCountUp(target);
  return (
    <div className={`rap2-summary-card rap2-summary-card--${variant}`}>
      <div className="rap2-summary-icon">{icon}</div>
      <div className="rap2-summary-value">{money ? cents(value) : value}</div>
      <div className="rap2-summary-label">{label}</div>
    </div>
  );
};

const RyanAmbassadorPage: React.FC = () => {
  const navigate = useNavigate();
  const { isRepresentative, ryanAccessLoading } = useRyanAuth();
  const [tab, setTab] = useState<Tab>('painel');
  const [data, setData] = useState<AmbassadorData | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedProductId, setCopiedProductId] = useState<string | null>(null);
  const [productPeriodFilter, setProductPeriodFilter] = useState<number | null>(null);

  const loadMetrics = useCallback(async (y: number) => {
    const r = await fetch(`/api/ryan/embaixador/metricas?year=${y}`, { headers: { Authorization: `Bearer ${tok()}` } });
    if (r.ok) setMetrics(await r.json());
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const meRes = await fetch('/api/ryan/embaixador/me', { headers: { Authorization: `Bearer ${tok()}` } });
      if (!meRes.ok) {
        setError('Você não tem um programa de embaixador ativo.');
        return;
      }
      setData(await meRes.json());
      await loadMetrics(year);
    } catch {
      setError('Erro de conexão.');
    } finally {
      setLoading(false);
    }
    // Roda só uma vez no mount: `year` aqui é o valor inicial (ano corrente).
    // Trocas de ano depois disso passam por handleYearChange → loadMetrics
    // diretamente, sem re-disparar o load completo (evita re-buscar /me e
    // reaparecer o "Carregando…" de página inteira a cada clique de navegação).
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleYearChange = (y: number) => {
    setYear(y);
    loadMetrics(y);
  };

  useEffect(() => {
    if (!ryanAccessLoading && !isRepresentative) navigate('/ryan', { replace: true });
  }, [ryanAccessLoading, isRepresentative, navigate]);

  const copyLink = (productId: string, link: string) => {
    navigator.clipboard.writeText(link).catch(() => {});
    setCopiedProductId(productId);
    setTimeout(() => setCopiedProductId(null), 2000);
  };

  return (
    <div className="rap2-page">
      <header className="rap2-header">
        <div className="rap2-header-inner">
          <div className="rap2-header-left">
            <button className="rap2-header-brand" onClick={() => navigate('/ryan')} title="Voltar">
              <img src="/Ryan/graia-logo-branca.png" alt="Voltar" className="rap2-header-logo" />
            </button>
            <span className="rap2-header-title">Painel do Embaixador</span>
          </div>

          <nav className="rap2-header-tabs">
            <button className={`rap2-tab${tab === 'painel' ? ' rap2-tab--active' : ''}`} onClick={() => setTab('painel')}>
              <LayoutDashboard size={16} />
              <span>Painel</span>
            </button>
            <button className={`rap2-tab${tab === 'produtos' ? ' rap2-tab--active' : ''}`} onClick={() => setTab('produtos')}>
              <Package size={16} />
              <span>Produtos</span>
            </button>
            <button className={`rap2-tab${tab === 'historico' ? ' rap2-tab--active' : ''}`} onClick={() => setTab('historico')}>
              <Receipt size={16} />
              <span>Histórico de Pagamentos</span>
            </button>
          </nav>
        </div>
      </header>

      <div className="rap2-content">
        {loading && <div className="rap2-empty">Carregando…</div>}
        {!loading && error && <div className="rap2-empty">{error}</div>}

        {!loading && data && tab === 'painel' && (
          <>
            <div className="rap2-greeting">
              <h2 className="rap2-greeting-title">Olá, {data.name}!</h2>
              <p className="rap2-greeting-subtitle">
                Cada venda pelo seu link te aproxima do próximo tier de comissão. Confira como está indo.
              </p>
            </div>

            <div className="rap2-summary-grid">
              <SummaryCard icon={<TrendingUp size={18} />} target={data.summary.totalSales} label="vendas geradas" variant="neutral" />
              <SummaryCard icon={<Wallet size={18} />} target={data.summary.totalEarnedCents} money label="total já ganho" variant="mint" />
              <SummaryCard icon={<Check size={18} />} target={data.summary.totalPaidCents} money label="saldo já pago" variant="blue" />
              <SummaryCard icon={<Wallet size={18} />} target={data.summary.totalPendingCents} money label="pendente de pagamento" variant="highlight" />
            </div>

            {metrics && (
              <>
                <div className="rap2-chart-block">
                  <div className="rap2-chart-block-title">Evolução semanal</div>
                  <RyanWeeklyBarChart data={metrics.weekly} color="#2F7130" />
                </div>
                <div className="rap2-chart-block">
                  <div className="rap2-chart-block-title">Evolução mensal</div>
                  <div className="rwc-card">
                    <RyanMonthlyLineChart data={metrics.monthly} year={year} onYearChange={handleYearChange} valueNoun="venda" />
                  </div>
                </div>
              </>
            )}

            {data.topProducts.length > 0 && (
              <div className="rap2-section">
                <div className="rap2-section-title"><Trophy size={16} /> Seus produtos mais vendidos</div>
                <div className="rap2-ranking">
                  {data.topProducts.map((p, i) => (
                    <div key={p.productId} className="rap2-ranking-row">
                      <span className="rap2-ranking-pos">{i + 1}º</span>
                      <span className="rap2-ranking-title">{p.title}</span>
                      <span className="rap2-ranking-sales">{p.salesCount} venda{p.salesCount !== 1 ? 's' : ''}</span>
                      <span className="rap2-ranking-earned">{cents(p.totalEarnedCents)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!loading && data && tab === 'produtos' && (() => {
          // Produto sem link (GET /embaixador/me só gera `link` quando o
          // produto tem publicSlug configurado) não tem página de divulgação
          // pra esse produto — mostrar o bloco inteiro sem link só confundia
          // o embaixador (parecia um produto quebrado, não um produto sem
          // permissão de divulgação). O card inteiro fica de fora da lista
          // aqui, não só a linha do link — `summary`/`topProducts` continuam
          // considerando TODOS os produtos vinculados (histórico de comissão
          // já gerado não deve sumir só porque o produto perdeu o link hoje).
          const divulgableProducts = data.products.filter(p => p.link);
          const periods = Array.from(new Set(divulgableProducts.map(p => p.periodo).filter((p): p is number => p != null))).sort((a, b) => a - b);
          const filteredProducts = productPeriodFilter === null
            ? divulgableProducts
            : divulgableProducts.filter(p => p.periodo === productPeriodFilter);
          return (
            <>
              {divulgableProducts.length === 0 && (
                <div className="rap2-empty">Você ainda não tem produtos vinculados ao seu programa.</div>
              )}

              {periods.length > 0 && (
                <div className="rap2-period-filter">
                  <button
                    className={`rap2-period-pill${productPeriodFilter === null ? ' rap2-period-pill--active' : ''}`}
                    onClick={() => setProductPeriodFilter(null)}
                  >
                    Todos
                  </button>
                  {periods.map(p => (
                    <button
                      key={p}
                      className={`rap2-period-pill${productPeriodFilter === p ? ' rap2-period-pill--active' : ''}`}
                      onClick={() => setProductPeriodFilter(p)}
                    >
                      {p}º período
                    </button>
                  ))}
                </div>
              )}

              {divulgableProducts.length > 0 && filteredProducts.length === 0 && (
                <div className="rap2-empty">Nenhum produto neste período.</div>
              )}

              {filteredProducts.map(p => (
                <div key={p.productId} className="rap2-card">
                  <div className="rap2-card-title-row">
                    <h2 className="rap2-card-title">
                      {p.title}
                      {p.periodo != null && <span className="rap2-period-badge">{p.periodo}º período</span>}
                    </h2>
                    <span className="rap2-tier-badge">{p.currentTierPercent}% de comissão</span>
                  </div>

                {p.link && (
                  <div className="rap2-link-row">
                    <code className="rap2-link-code">{p.link}</code>
                    <button className="rap2-copy-btn" onClick={() => copyLink(p.productId, p.link!)}>
                      {copiedProductId === p.productId ? <><Check size={13} /> Copiado</> : <><Copy size={13} /> Copiar</>}
                    </button>
                  </div>
                )}

                <div className="rap2-stats-grid">
                  <div className="rap2-stat">
                    <TrendingUp size={18} />
                    <div>
                      <div className="rap2-stat-value">{p.salesCount}</div>
                      <div className="rap2-stat-label">vendas geradas</div>
                    </div>
                  </div>
                  <div className="rap2-stat">
                    <Wallet size={18} />
                    <div>
                      <div className="rap2-stat-value">{cents(p.totalEarnedCents)}</div>
                      <div className="rap2-stat-label">total ganho</div>
                    </div>
                  </div>
                  <div className="rap2-stat">
                    <Wallet size={18} />
                    <div>
                      <div className="rap2-stat-value">{cents(p.totalPendingCents)}</div>
                      <div className="rap2-stat-label">pendente</div>
                    </div>
                  </div>
                  <div className="rap2-stat">
                    <Wallet size={18} />
                    <div>
                      <div className="rap2-stat-value">{cents(p.totalPaidCents)}</div>
                      <div className="rap2-stat-label">já pago</div>
                    </div>
                  </div>
                </div>

                <div className="rap2-roadmap">
                  <div className="rap2-roadmap-track">
                    {p.tiers.map(tier => (
                      <div
                        key={tier.minSales}
                        className={`rap2-roadmap-segment${tier.reached ? ' reached' : ''}${tier.current ? ' current' : ''}`}
                      >
                        <span className="rap2-roadmap-percent">{tier.percent}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="rap2-roadmap-labels">
                    {p.tiers.map(tier => (
                      <div key={tier.minSales} className="rap2-roadmap-label">
                        <span>a partir da {tier.minSales}ª venda</span>
                        {tier.current && <span className="rap2-roadmap-here">você está aqui</span>}
                      </div>
                    ))}
                  </div>
                </div>

                  {p.nextTierPercent != null && (
                    <div className="rap2-next-tier">
                      <Target size={14} />
                      Faltam <strong>{p.nextTierRemaining}</strong> vendas para chegar a <strong>{p.nextTierPercent}%</strong> de comissão.
                    </div>
                  )}
                </div>
              ))}
            </>
          );
        })()}

        {!loading && data && tab === 'historico' && (
          <div className="rap2-section rap2-section--table">
            <div className="rap2-section-title"><Receipt size={16} /> Histórico de pagamentos</div>
            {data.payoutHistory.length === 0 ? (
              <div className="rap2-empty">Nenhum pagamento registrado ainda.</div>
            ) : (
              <div className="rap2-table-wrap">
                <table className="rap2-table">
                  <thead>
                    <tr>
                      <th>Valor pago</th>
                      <th>Data</th>
                      <th>Horário</th>
                      <th>Vendas cobertas</th>
                      <th>Observação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payoutHistory.map(payout => (
                      <tr key={payout._id}>
                        <td className="rap2-table-amount">{cents(payout.amountCents)}</td>
                        <td>{fmtDateOnly(payout.paidAt)}</td>
                        <td>{fmtTimeOnly(payout.paidAt)}</td>
                        <td>{payout.salesCount}</td>
                        <td>{payout.note || (payout.legacy ? 'Pagamento legado' : '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RyanAmbassadorPage;
